import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  Network,
  School,
  IdCardLanyard,
  UsersRound,
  Clock,
} from "lucide-react";
import { useSelector } from "react-redux";
import Avatar from "../components/Avatar";
import {
  useGetUserByEmailQuery,
  useGetAcademyQuery,
  useGetDepartmentQuery,
  useGetDivisionsQuery,
} from "../slices/userApiSlice";
import { useGetOvertimeByUserIdQuery } from "../slices/overtimeApiSlice";
import { useGetAttendanceByUserIdQuery } from "../slices/attendanceApiSlice";
import { useGetLeavesByUserIdQuery } from "../slices/leaveApiSlice";
import {
  getAcademyName,
  getDepartmentName,
  getDivisionName,
} from "../utils/userManagementUtils";
import LateReasonDialog from "../components/LateReasonDialog/LateReasonDialog";

export default function UserDetails() {
  const { email } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overtime");
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [selectedLateReason, setSelectedLateReason] = useState("");

  // Fetch user data
  const { data: userData, isLoading: isLoadingUser } = useGetUserByEmailQuery(
    email,
    {
      skip: !email,
    },
  );

  const user = userData?.user;
  const userRole = user?.role?.name || user?.role || "";

  // Fetch academies, departments, divisions for display
  const { data: academiesData } = useGetAcademyQuery();
  const academies = academiesData || [];

  const { data: departmentsData } = useGetDepartmentQuery();
  const departments = departmentsData || [];

  const { data: divisionsData } = useGetDivisionsQuery();
  const divisions = divisionsData || [];

  // Get academy, department, division names using utility functions
  const academyName = useMemo(() => {
    if (!user?.academyId) return "N/A";
    const name = getAcademyName(user.academyId, academies);
    return name !== "Unknown Academy" ? name : "N/A";
  }, [user?.academyId, academies]);

  const departmentName = useMemo(() => {
    if (!user?.departmentId) return "N/A";
    const name = getDepartmentName(user.departmentId, departments);
    return name !== "Unknown department" ? name : "N/A";
  }, [user?.departmentId, departments]);

  const divisionName = useMemo(() => {
    if (!user?.divisionId) return "N/A";
    return getDivisionName(user.divisionId, divisions);
  }, [user?.divisionId, divisions]);

  // Get userId for API queries
  const userId = user?.userId || user?._id || user?.id || "";

  // Fetch overtime records by userId
  const { data: overtimeData, isLoading: isLoadingOvertime } =
    useGetOvertimeByUserIdQuery(userId, {
      skip: !userId,
    });

  const overtimeRecords = useMemo(() => {
    if (
      !overtimeData ||
      !overtimeData.requests ||
      !Array.isArray(overtimeData.requests)
    )
      return [];

    const formatDate = (isoDateTime) => {
      if (!isoDateTime) return "";
      const date = new Date(isoDateTime);
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${dateStr} - ${timeStr}`;
    };

    const statusDisplay = {
      PENDING: "Pending",
      REAPPLY: "Re-Apply",
      REAPPLIED: "Re-Applied",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      APPROVED: "Approved",
    };

    return overtimeData.requests
      .map((item) => {
        const details = item.details || {};
        return {
          id: details.id || item.overtimeId || item.id,
          from: formatDate(details.startTime),
          to: formatDate(details.endTime),
          description: details.description || "No description",
          hours: `${Math.round(details.totalHours || 0)} hrs`,
          status: statusDisplay[item.status] || item.status || "Pending",
          submissionDate:
            details.submittedAt || item.createdAt || new Date().toISOString(),
        };
      })
      .sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate));
  }, [overtimeData]);

  // Fetch attendance history by userId
  const { data: attendanceData, isLoading: isLoadingAttendance } =
    useGetAttendanceByUserIdQuery(userId, {
      skip: !userId,
    });

  const attendanceRecords = useMemo(() => {
    if (
      !attendanceData ||
      !attendanceData.content ||
      !Array.isArray(attendanceData.content)
    )
      return [];

    return attendanceData.content
      .map((record) => ({
        id: record.id,
        date: record.date,
        clockIn: record.clock_in_time
          ? new Date(record.clock_in_time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Asia/Thimphu",
            })
          : null,
        clockOut: record.clock_out_time
          ? new Date(record.clock_out_time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              timeZone: "Asia/Thimphu",
            })
          : null,
        type:
          record.attendance_status === "PRESENT"
            ? "present"
            : record.attendance_status === "LEAVE"
              ? "leave"
              : "half",
        attendance_status: record.attendance_status,
        late_reason: record.late_reason || null,
        clock_in_time: record.clock_in_time,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [attendanceData]);

  // Fetch leave records by userId
  const { data: leaveData, isLoading: isLoadingLeave } =
    useGetLeavesByUserIdQuery(userId, {
      skip: !userId,
    });

  const leaveRecords = useMemo(() => {
    if (!leaveData || !leaveData.content || !Array.isArray(leaveData.content))
      return [];

    return leaveData.content
      .map((leave) => {
        // Use totalDays from API if available, otherwise calculate
        const days =
          leave.totalDays !== null && leave.totalDays !== undefined
            ? leave.totalDays
            : (() => {
                if (leave.startDate && leave.endDate) {
                  const start = new Date(leave.startDate);
                  const end = new Date(leave.endDate);
                  const diffTime = Math.abs(end - start);
                  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                }
                return 1;
              })();

        const isManualLeave =
          leave.leaveType !== "CASUAL" && leave.leaveType !== "EARN";
        const leaveTypeDisplay = isManualLeave
          ? "Manual Leave"
          : leave.leaveType === "CASUAL"
            ? "Casual Leave"
            : leave.leaveType === "EARN"
              ? "Earned Leave"
              : leave.leaveType || "Leave";

        return {
          id: leave._id || leave.id,
          type: leaveTypeDisplay,
          days: days,
          startDate: leave.startDate,
          endDate: leave.endDate,
          isSingleDay: days === 1 || days === 0.5,
          status: leave.status || "Pending",
          submittedDate: leave.submittedAt || leave.createdAt,
        };
      })
      .sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));
  }, [leaveData]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase() || "";
    if (statusUpper.includes("APPROVED")) return "bg-green-100 text-green-800";
    if (statusUpper.includes("REJECTED")) return "bg-red-100 text-red-800";
    if (statusUpper.includes("PENDING")) return "bg-yellow-100 text-yellow-800";
    if (statusUpper.includes("FORWARDED")) return "bg-blue-100 text-blue-800";
    return "bg-zinc-100 text-zinc-800";
  };

  if (isLoadingUser) {
    return (
      <div className="bg-white py-6 px-8 h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-zinc-600">Loading user details...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="bg-white py-6 px-8 h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">User not found</div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-black text-white rounded hover:bg-zinc-800"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Determine which affiliations to show based on role
  const showAcademy = ![
    "admin",
    "Admin",
    "super_admin",
    "Super Admin",
  ].includes(userRole);
  const showDepartment = ![
    "admin",
    "Admin",
    "super_admin",
    "Super Admin",
    "cfm",
    "CFM",
  ].includes(userRole);
  const showDivision = ![
    "admin",
    "Admin",
    "super_admin",
    "Super Admin",
    "cfm",
    "CFM",
    "Manager",
    "manager",
  ].includes(userRole);

  // Determine if tabs should be shown (hide for admin and super admin)
  const showTabs = !["admin", "Admin", "super_admin", "Super Admin"].includes(
    userRole,
  );

  return (
    <div className="bg-white min-h-full">
      {/* Header Section */}
      <div className="bg-white border-b border-zinc-200 px-8 py-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <h1 className="text-2xl font-bold text-zinc-900">User Details</h1>
        </div>
      </div>

      <div className="px-8 py-6">
        {/* User Profile Section */}
        <div className="overflow-hidden">
          <div className="flex flex-col max-w-lg w-full gap-4">
            {/* Profile Header */}
            <div className="bg-white border rounded-2xl shadow-sm overflow-hidden flex items-center gap-4 p-4">
              <div className="flex-shrink-0 relative">
                <Avatar user={user} size={52} />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-zinc-900">
                    {user.name}
                  </h2>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                    {userRole}
                  </span>
                </div>
                <p className="text-zinc-400 text-sm">{user.email}</p>
              </div>
            </div>

            {/* Profile Details - Apple Style */}
            <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden p-4 py-2">
              {/* Employee ID */}
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-zinc-400 flex items-center justify-center">
                  <IdCardLanyard className="w-4 h-4 text-white" />
                </div>
                <div
                  className={`flex items-center justify-between flex-1 py-3.5 ${
                    showAcademy || showDepartment || showDivision
                      ? "border-b border-zinc-200"
                      : ""
                  }`}
                >
                  <span className="text-sm text-zinc-900">Employee ID</span>
                  <span className="text-sm text-zinc-400">
                    {user.employeeId || "N/A"}
                  </span>
                </div>
              </div>

              {/* Academy */}
              {showAcademy && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                    <School className="w-4 h-4 text-white" />
                  </div>
                  <div
                    className={`flex items-center justify-between flex-1 py-3.5 ${
                      showDepartment || showDivision
                        ? "border-b border-zinc-200"
                        : ""
                    }`}
                  >
                    <span className="text-sm text-zinc-900">Academy</span>
                    <span className="text-sm text-zinc-400">{academyName}</span>
                  </div>
                </div>
              )}

              {/* Department */}
              {showDepartment && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Network className="w-4 h-4 text-white" />
                  </div>
                  <div
                    className={`flex items-center justify-between flex-1 py-3.5 ${
                      showDivision ? "border-b border-zinc-200" : ""
                    }`}
                  >
                    <span className="text-sm text-zinc-900">Department</span>
                    <span className="text-sm text-zinc-400">
                      {departmentName}
                    </span>
                  </div>
                </div>
              )}

              {/* Division */}
              {showDivision && (
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                    <UsersRound className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex items-center justify-between flex-1 py-3.5">
                    <span className="text-sm text-zinc-900">Division</span>
                    <span className="text-sm text-zinc-400">
                      {divisionName}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          {showTabs && (
            <div className="mt-6">
              <div className="border-b border-zinc-200">
                <div className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab("overtime")}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "overtime"
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-zinc-600 hover:text-zinc-800"
                    }`}
                  >
                    Overtime Records
                  </button>
                  <button
                    onClick={() => setActiveTab("attendance")}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "attendance"
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-zinc-600 hover:text-zinc-800"
                    }`}
                  >
                    Attendance History
                  </button>
                  <button
                    onClick={() => setActiveTab("leave")}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === "leave"
                        ? "border-blue-600 text-blue-600"
                        : "border-transparent text-zinc-600 hover:text-zinc-800"
                    }`}
                  >
                    Leave Records
                  </button>
                </div>
              </div>

              {/* Tab Content */}
              <div className="mt-6 border border-zinc-200 rounded-lg overflow-hidden">
                {activeTab === "overtime" && (
                  <div>
                    {isLoadingOvertime ? (
                      <div className="text-center py-8 text-zinc-500">
                        Loading overtime records...
                      </div>
                    ) : overtimeRecords.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No overtime records found
                      </div>
                    ) : (
                      <div className="bg-white overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-max">
                            <thead className="bg-zinc-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  From
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  To
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Hours
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Description
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-zinc-200">
                              {overtimeRecords.map((record) => (
                                <tr
                                  key={record.id}
                                  className="hover:bg-zinc-50"
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {record.from.split(" - ")[0]}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {record.to.split(" - ")[0]}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {record.hours}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                        record.status,
                                      )}`}
                                    >
                                      {record.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-zinc-900 max-w-xs truncate">
                                    {record.description}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "attendance" && (
                  <div>
                    {isLoadingAttendance ? (
                      <div className="text-center py-8 text-zinc-500">
                        Loading attendance records...
                      </div>
                    ) : attendanceRecords.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No attendance records found
                      </div>
                    ) : (
                      <div className="bg-white overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-max">
                            <thead className="bg-zinc-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Clock In
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Clock Out
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-zinc-200">
                              {attendanceRecords.map((record) => (
                                <tr
                                  key={record.id}
                                  className="hover:bg-zinc-50 cursor-pointer transition-colors"
                                  onClick={() => {
                                    // Navigate to attendance record detail using the record ID
                                    if (!record.id) return; // Don't navigate if no ID
                                    // Get base path by going up from /users/email to base route
                                    const basePath =
                                      location.pathname.split("/users/")[0];
                                    navigate(
                                      `${basePath}/attendance/record/${record.id}`,
                                    );
                                  }}
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                                    {new Date(
                                      record.date + "T00:00:00",
                                    ).toLocaleDateString("en-GB", {
                                      timeZone: "Asia/Thimphu",
                                    })}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                                          record.type === "leave"
                                            ? "bg-zinc-100 text-zinc-700"
                                            : record.type === "half"
                                              ? "bg-yellow-100 text-yellow-800"
                                              : "bg-blue-100 text-black"
                                        }`}
                                      >
                                        {record.type === "leave"
                                          ? "Leave"
                                          : record.type === "half"
                                            ? "Half Day"
                                            : "Present"}
                                      </span>
                                      {record.late_reason && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedLateReason(
                                              record.late_reason,
                                            );
                                            setShowLateReasonDialog(true);
                                          }}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full whitespace-nowrap hover:bg-orange-100 transition-colors cursor-pointer"
                                          title="View late arrival reason"
                                        >
                                          <Clock size={12} />
                                          Late
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                                    {record.clockIn || "-"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                                    {record.clockOut || "-"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "leave" && (
                  <div>
                    {isLoadingLeave ? (
                      <div className="text-center py-8 text-zinc-500">
                        Loading leave records...
                      </div>
                    ) : leaveRecords.length === 0 ? (
                      <div className="text-center py-8 text-zinc-500">
                        No leave records found
                      </div>
                    ) : (
                      <div className="bg-white overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-max">
                            <thead className="bg-zinc-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Type
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Days
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Submitted
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Start Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  End Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-zinc-200">
                              {leaveRecords.map((record) => (
                                <tr
                                  key={record.id}
                                  className="hover:bg-zinc-50"
                                >
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                                    {record.type}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {record.days}{" "}
                                    {record.days === 1 ? "day" : "days"}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {formatDateTime(record.submittedDate)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {formatDate(record.startDate)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                                    {record.isSingleDay
                                      ? formatDate(record.startDate)
                                      : formatDate(record.endDate)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                        record.status,
                                      )}`}
                                    >
                                      {record.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <LateReasonDialog
        show={showLateReasonDialog}
        onClose={() => setShowLateReasonDialog(false)}
        lateReason={selectedLateReason}
      />
    </div>
  );
}
